import { z } from 'zod'
import type { Hex } from '../../types'
import { bigintTextSchema, lowerHexSchema } from '../../types/primitive'
import type { TransactionIndex } from '../../types/transactionIndex'
import type { HeadersInput } from '../types'
import type { RoutescanClient } from './client'

type GetListOfNormalTransactionsByAddressParameters = {
  client: RoutescanClient
  address: Hex
  startblock?: number
  endblock?: number
  page?: number
  offset?: number
  sort?: 'asc' | 'desc'
  headers?: HeadersInput
}

const transactionSchema = z.object({
  blockNumber: z.string().regex(/^\d+$/).transform(Number),
  hash: z.union([
    lowerHexSchema,
    z
      .string()
      .regex(/^GENESIS_[0-9a-f]*$/)
      .transform((v) => v.replace(/^GENESIS_/, '0x'))
      .pipe(lowerHexSchema),
  ]),
  timeStamp: z.string().regex(/^\d+$/).transform(Number),
  input: z.union([
    lowerHexSchema,
    z
      .literal('')
      .transform(() => '0x')
      .pipe(lowerHexSchema),
    // Blast など一部の chain は input を返さず "n/a" になるため、未取得として扱う。
    z
      .literal('n/a')
      .transform(() => undefined),
  ]),
  value: bigintTextSchema,
})
const resultSchema = z.array(transactionSchema)

/** {@link https://docs.etherscan.io/routescan/api-endpoints/accounts#get-a-list-of-normal-transactions-by-address} */
export const getListOfNormalTransactionsByAddress = async ({
  client,
  address,
  startblock = 0,
  endblock,
  page = 1,
  offset = 10_000,
  sort = 'asc',
  headers,
}: GetListOfNormalTransactionsByAddressParameters): Promise<
  TransactionIndex[]
> => {
  try {
    const result = await client.get({
      path: '',
      query: {
        module: 'account',
        action: 'txlist',
        address,
        startblock: startblock.toString(),
        ...(endblock === undefined
          ? {}
          : {
              endblock: endblock.toString(),
            }),
        page: page.toString(),
        offset: offset.toString(),
        sort,
      },
      resultSchema,
      headers,
    })
    return result.map((v) => ({
      blockNumber: v.blockNumber,
      hash: v.hash,
      timestamp: v.timeStamp * 1000,
      input: v.input,
      value: v.value,
    }))
  } catch (error) {
    if (
      error instanceof Error &&
      (error.message === 'No transactions found' ||
        error.message === 'No records found')
    ) {
      return []
    }
    throw error
  }
}
